'use client';
import { useState } from 'react';
import { getTopicData } from './TopicModal';

/* ─── Build a flat "top picks" list from all topics ──────────────────────────
   We pick the first 2 FAQs from each topic = 12 total in the "Top Picks" tab,
   then display the full list grouped by topic.                                */
const ALL_CATEGORIES = [
    { key: 'all',          label: 'Top Picks',    icon: 'pi pi-star',          color: '#f59e0b' },
    { key: 'orders',       label: 'Orders',       icon: 'pi pi-box',           color: '#6366f1' },
    { key: 'returns',      label: 'Returns',      icon: 'pi pi-refresh',       color: '#f97316' },
    { key: 'warranty',     label: 'Warranty',     icon: 'pi pi-shield',        color: '#10b981' },
    { key: 'installation', label: 'Installation', icon: 'pi pi-wrench',        color: '#f59e0b' },
    { key: 'app',          label: 'App & Wi-Fi',  icon: 'pi pi-mobile',        color: '#0ea5e9' },
    { key: 'products',     label: 'Products',     icon: 'pi pi-shopping-cart', color: '#8b5cf6' },
];


/* ─── Accordion Item ─────────────────────────────────────────────────────────── */
const AccordionItem = ({ faq, color, index, defaultOpen = false }) => {
    const [open, setOpen] = useState(defaultOpen);

    return (
        <div
            className="border-round-xl overflow-hidden"
            style={{
                border: `1.5px solid ${open ? color + '40' : 'var(--surface-border)'}`,
                background: open ? `${color}06` : 'var(--surface-card)',
                transition: 'all 0.25s ease',
                boxShadow: open ? `0 4px 20px ${color}15` : 'none',
            }}
        >
            {/* Question row */}
            <button
                className="w-full text-left border-none"
                style={{
                    background: 'transparent',
                    cursor: 'pointer',
                    padding: '1rem 1.25rem',
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '0.85rem',
                }}
                onClick={() => setOpen((o) => !o)}
            >
                {/* Number badge */}
                <div
                    className="flex align-items-center justify-content-center flex-shrink-0 border-circle font-bold"
                    style={{
                        width: 26, height: 26,
                        background: open ? color : `${color}15`,
                        color: open ? '#fff' : color,
                        fontSize: '0.7rem',
                        marginTop: '0.05rem',
                        transition: 'all 0.2s',
                        flexShrink: 0,
                    }}
                >
                    {index + 1}
                </div>

                <span
                    className="flex-1 font-semibold"
                    style={{
                        fontSize: '0.9rem',
                        color: open ? color : 'var(--text-color)',
                        lineHeight: 1.5,
                        transition: 'color 0.2s',
                    }}
                >
                    {faq.q}
                </span>

                {/* Chevron */}
                <i
                    className={`pi pi-chevron-${open ? 'up' : 'down'} flex-shrink-0`}
                    style={{
                        fontSize: '0.7rem',
                        color: open ? color : 'var(--text-color-secondary)',
                        marginTop: '0.3rem',
                        transition: 'transform 0.2s, color 0.2s',
                    }}
                />
            </button>

            {/* Answer — animated */}
            <div
                style={{
                    maxHeight: open ? '500px' : '0',
                    overflow: 'hidden',
                    transition: 'max-height 0.3s ease',
                }}
            >
                <div
                    className="text-700"
                    style={{
                        padding: '0 1.25rem 1.25rem 3.6rem',
                        fontSize: '0.87rem',
                        lineHeight: 1.8,
                    }}
                >
                    {faq.a}
                </div>
            </div>
        </div>
    );
};

/* ─── Category tab ────────────────────────────────────────────────────────────── */
const CategoryTab = ({ cat, active, onClick }) => (
    <button
        onClick={() => onClick(cat.key)}
        style={{
            padding: '0.5rem 1.1rem',
            borderRadius: '999px',
            border: `1.5px solid ${active ? cat.color : 'var(--surface-border)'}`,
            background: active ? `${cat.color}15` : 'var(--surface-card)',
            color: active ? cat.color : 'var(--text-color-secondary)',
            cursor: 'pointer',
            fontWeight: active ? 700 : 500,
            fontSize: '0.8rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.4rem',
            whiteSpace: 'nowrap',
            transition: 'all 0.2s',
            boxShadow: active ? `0 2px 10px ${cat.color}25` : 'none',
        }}
    >
        <i className={cat.icon} style={{ fontSize: '0.8rem' }} />
        {cat.label}
    </button>
);

/* ─── FAQSection ─────────────────────────────────────────────────────────────── */
export const FAQSection = ({ replacementAllow = 7 }) => {
    const [activeTab, setActiveTab] = useState('all');

    const topicData = getTopicData(replacementAllow);
    const topPicks = Object.values(topicData).flatMap((t) =>
        t.faqs.slice(0, 2).map((faq) => ({ ...faq, topicColor: t.color, topicTitle: t.title, topicIcon: t.icon }))
    );

    const activeCat = ALL_CATEGORIES.find((c) => c.key === activeTab) || ALL_CATEGORIES[0];

    const faqs = activeTab === 'all'
        ? topPicks
        : topicData[activeTab]?.faqs.map((faq) => ({
            ...faq,
            topicColor: topicData[activeTab].color,
        })) || [];

    const color = activeCat.color;

    return (
        <section
            style={{
                background: 'var(--surface-50)',
                borderTop: '1px solid var(--surface-border)',
                borderBottom: '1px solid var(--surface-border)',
                padding: '3rem 0',
            }}
        >
            <div className="container" style={{ padding: '0 1rem' }}>

                {/* Section heading */}
                <div className="flex align-items-center gap-3 mb-4">
                    <div
                        className="flex align-items-center justify-content-center border-circle flex-shrink-0"
                        style={{
                            width: 40, height: 40,
                            background: 'linear-gradient(135deg, rgba(245,158,11,0.15), rgba(249,115,22,0.15))',
                            border: '1.5px solid rgba(245,158,11,0.3)',
                        }}
                    >
                        <i className="pi pi-question-circle" style={{ fontSize: '1rem', color: '#f59e0b' }} />
                    </div>
                    <div>
                        <h2 className="m-0 font-bold text-900" style={{ fontSize: '1.25rem' }}>
                            Frequently Asked Questions
                        </h2>
                        <p className="m-0 text-500" style={{ fontSize: '0.8rem' }}>
                            {Object.values(topicData).reduce((sum, t) => sum + t.faqs.length, 0)} questions answered across all topics
                        </p>
                    </div>
                </div>

                {/* Category filter tabs */}
                <div
                    className="flex gap-2 mb-4"
                    style={{
                        overflowX: 'auto',
                        paddingBottom: '0.5rem',
                        scrollbarWidth: 'none',
                        msOverflowStyle: 'none',
                    }}
                >
                    {ALL_CATEGORIES.map((cat) => (
                        <CategoryTab
                            key={cat.key}
                            cat={cat}
                            active={activeTab === cat.key}
                            onClick={setActiveTab}
                        />
                    ))}
                </div>

                {/* Results count */}
                <div className="flex align-items-center gap-2 mb-3">
                    <div
                        className="flex align-items-center justify-content-center border-circle"
                        style={{ width: 22, height: 22, background: `${color}15` }}
                    >
                        <i className={activeCat.icon} style={{ fontSize: '0.65rem', color }} />
                    </div>
                    <span className="text-sm font-semibold" style={{ color }}>
                        {activeCat.label}
                    </span>
                    <span className="text-sm text-500">
                        — {faqs.length} question{faqs.length !== 1 ? 's' : ''}
                    </span>
                </div>

                {/* FAQ list */}
                <div className="flex flex-column gap-2">
                    {faqs.map((faq, i) => (
                        <AccordionItem
                            key={`${activeTab}-${i}`}
                            faq={faq}
                            color={faq.topicColor || color}
                            index={i}
                            defaultOpen={i === 0}
                        />
                    ))}
                </div>
            </div>
        </section>
    );
};
